import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import { namesToFullName } from '@nova/core';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { reactiveOption } from '@satellite/composables/presenterV2ReactiveOption';

import type { ComputedRef, Ref } from 'vue';
import type {
  PresenterV2DisplayConfigOptions,
  PresenterV2DisplayOverrideProps
} from '@satellite/composables/presenterV2DisplayOptions';
import type { CarrierUserDetails } from './carrierPresenterV2Shared';

export type CarrierDisplayField = 'name' | 'email' | 'phone' | 'companyName' | 'companyScac';

export interface UseCarrierDisplayV2Options extends PresenterV2DisplayConfigOptions {
  nameProps?: PresenterV2DisplayOverrideProps;
  emailProps?: PresenterV2DisplayOverrideProps;
  phoneProps?: PresenterV2DisplayOverrideProps;
  companyNameProps?: PresenterV2DisplayOverrideProps;
  companyScacProps?: PresenterV2DisplayOverrideProps;
}

export function useCarrierDisplayV2(
  carrier: Ref<CarrierUserDetails | null> | ComputedRef<CarrierUserDetails | null>,
  options: UseCarrierDisplayV2Options = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterV2Mode.Live);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const carrierName = computed(() => {
    if (carrier.value?.name) {
      return carrier.value.name;
    }

    if (carrier.value?.firstName || carrier.value?.lastName) {
      return namesToFullName(carrier.value?.firstName, carrier.value?.lastName);
    }

    return null;
  });

  const carrierEmail = computed(() => carrier.value?.email ?? null);
  const carrierPhone = computed(() => carrier.value?.phone || '');
  const companyName = computed(() => carrier.value?.company?.name ?? null);
  const companyScac = computed(() => carrier.value?.company?.scac ?? null);

  const carrierNameAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-name' }, toValue(options.nameProps) ?? {})
  );
  const carrierEmailAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-email' }, toValue(options.emailProps) ?? {})
  );
  const carrierPhoneAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-phone' }, toValue(options.phoneProps) ?? {})
  );
  const companyNameAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'company-presenter-v2-company-name' },
      toValue(options.companyNameProps) ?? {}
    )
  );
  const companyScacAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'company-presenter-v2-company-scac' },
      toValue(options.companyScacProps) ?? {}
    )
  );

  const unknownCompanyMessage = computed(() => t('core.yard.messages.unknownCompany'));

  return reactive({
    carrier,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    carrierName,
    carrierEmail,
    carrierPhone,
    companyName,
    companyScac,
    carrierNameAttrs,
    carrierEmailAttrs,
    carrierPhoneAttrs,
    companyNameAttrs,
    companyScacAttrs,
    unknownCompanyMessage
  });
}

export type CarrierDisplayV2Data = ReturnType<typeof useCarrierDisplayV2>;
